import React, { useRef, useState } from 'react';
import { agentsApi } from '../../../api/client';
import Modal, { Button, Badge } from '../../ui/Modal';
import { ErrorBanner } from '../../../auth/AuthShell';

const COLUMNS = [
  'extension', 'name', 'email', 'queues', 'winid', 'skill_level',
  'callerid_name', 'callerid_number', 'language', 'recording',
  'wrap_time', 'max_calls', 'enabled',
];

const SAMPLE = `extension,name,email,queues,winid,skill_level,callerid_name,callerid_number,language,recording,wrap_time,max_calls,enabled
60101,Alice Example,alice@continuumgbl.com,sales|support,WIN-101,3,,,en,true,15,1,true
60102,Bob Example,bob@continuumgbl.com,sales,WIN-102,2,,,en,true,15,1,true
`;

export default function CsvImportWizard({ onClose, onDone }) {
  const fileRef = useRef(null);
  const [file, setFile] = useState(null);
  const [dryResult, setDryResult] = useState(null);
  const [commitResult, setCommitResult] = useState(null);
  const [error, setError] = useState(null);
  const [busy, setBusy] = useState(false);

  const onPick = (e) => {
    const f = e.target.files?.[0];
    setFile(f || null);
    setDryResult(null);
    setCommitResult(null);
    setError(null);
  };

  const runDryRun = async () => {
    if (!file) return;
    setBusy(true); setError(null); setCommitResult(null);
    try {
      const res = await agentsApi.importDryRun(file);
      setDryResult(res);
    } catch (err) {
      setError(err?.detail || err?.message || 'Dry-run failed.');
    } finally {
      setBusy(false);
    }
  };

  const runCommit = async () => {
    if (!file) return;
    if (!dryResult) return;
    if (!confirm(
      `Apply ${dryResult.summary.create} creates and ${dryResult.summary.update} updates?\n\n` +
      `${dryResult.summary.error || 0} error rows will be skipped.`
    )) return;
    setBusy(true); setError(null);
    try {
      const res = await agentsApi.importCommit(file);
      setCommitResult(res);
    } catch (err) {
      setError(err?.detail || err?.message || 'Commit failed.');
    } finally {
      setBusy(false);
    }
  };

  const downloadSample = () => {
    const blob = new Blob([SAMPLE], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url; a.download = 'continuumcx-agents-sample.csv';
    a.click();
    URL.revokeObjectURL(url);
  };

  const result = commitResult || dryResult;

  return (
    <Modal
      open
      onClose={onClose}
      size="xl"
      title="Import agents from CSV"
      footer={
        <>
          <Button onClick={onClose}>{commitResult ? 'Close' : 'Cancel'}</Button>
          {!commitResult && dryResult && (
            <Button
              variant="primary"
              onClick={runCommit}
              loading={busy}
              disabled={dryResult.summary.create + dryResult.summary.update === 0}
            >
              Apply changes
            </Button>
          )}
          {!commitResult && !dryResult && (
            <Button variant="primary" onClick={runDryRun} loading={busy} disabled={!file}>
              Preview changes
            </Button>
          )}
          {commitResult && (
            <Button variant="primary" onClick={onDone}>Done</Button>
          )}
        </>
      }
    >
      <ErrorBanner>{error}</ErrorBanner>

      {!result && (
        <div className="space-y-4">
          <div className="text-sm">
            Upload a CSV with the following columns:
            <div className="mt-1 font-mono text-xs p-2 rounded-thin bg-surface-subtle dark:bg-surface-dark-subtle break-all">
              {COLUMNS.join(',')}
            </div>
            <ul className="mt-2 text-xs text-surface-muted list-disc list-inside">
              <li>Emails must end with <code>@continuumgbl.com</code>.</li>
              <li>Extensions are 4–6 digits. Existing rows are matched on extension+email together.</li>
              <li><code>queues</code> is pipe-delimited (e.g. <code>sales|support</code>).</li>
              <li><code>recording</code> and <code>enabled</code> accept true/false/yes/no/1/0.</li>
              <li>SIP passwords are auto-generated server-side; do not include them.</li>
            </ul>
            <button
              type="button"
              onClick={downloadSample}
              className="mt-2 text-xs underline text-continuum-500 hover:text-continuum-600"
            >Download sample CSV</button>
          </div>

          <div>
            <input
              ref={fileRef}
              type="file"
              accept=".csv,text/csv"
              onChange={onPick}
              className="block w-full text-sm file:mr-3 file:py-2 file:px-3 file:rounded-thin file:border-0
                         file:bg-continuum-500 file:text-white file:cursor-pointer
                         file:hover:bg-continuum-600"
            />
            {file && (
              <p className="mt-1 text-xs text-surface-muted">Selected: {file.name} ({file.size} bytes)</p>
            )}
          </div>
        </div>
      )}

      {result && (
        <div>
          <div className="flex gap-2 mb-3 flex-wrap">
            <Badge tone="blue">Total: {result.summary.total}</Badge>
            <Badge tone="green">Create: {commitResult?.summary?.created ?? result.summary.create}</Badge>
            <Badge tone="amber">Update: {commitResult?.summary?.updated ?? result.summary.update}</Badge>
            <Badge>Skip: {commitResult?.summary?.skipped ?? result.summary.skip}</Badge>
            <Badge tone="red">Error: {commitResult?.summary?.errors ?? result.summary.error}</Badge>
            {commitResult && <Badge tone="green">✓ Applied</Badge>}
          </div>

          <div className="border border-surface-border dark:border-surface-dark-border rounded-thin overflow-auto max-h-[50vh]">
            <table className="w-full text-xs">
              <thead className="bg-surface-subtle dark:bg-surface-dark-subtle sticky top-0">
                <tr className="text-left">
                  <th className="px-2 py-1.5">Line</th>
                  <th className="px-2 py-1.5">Action</th>
                  <th className="px-2 py-1.5">Extension</th>
                  <th className="px-2 py-1.5">Email</th>
                  <th className="px-2 py-1.5">Detail</th>
                </tr>
              </thead>
              <tbody>
                {result.rows.map((r, i) => (
                  <tr key={i} className="border-t border-surface-border dark:border-surface-dark-border align-top">
                    <td className="px-2 py-1.5 font-mono">{r.line}</td>
                    <td className="px-2 py-1.5">
                      {r.action === 'create' ? <Badge tone="green">create</Badge> :
                       r.action === 'update' ? <Badge tone="amber">update</Badge> :
                       r.action === 'skip'   ? <Badge>skip</Badge> :
                                                <Badge tone="red">error</Badge>}
                    </td>
                    <td className="px-2 py-1.5 font-mono">{r.extension || '—'}</td>
                    <td className="px-2 py-1.5">{r.email || '—'}</td>
                    <td className="px-2 py-1.5">
                      {r.reason && <span className="text-red-600 dark:text-red-300">{r.reason}</span>}
                      {r.diff && (
                        <div className="text-xs space-y-0.5">
                          {Object.entries(r.diff).map(([k, [from, to]]) => (
                            <div key={k}>
                              <code className="text-surface-muted">{k}</code>:{' '}
                              <span className="line-through text-surface-muted">{JSON.stringify(from)}</span>
                              {' → '}
                              <span className="font-medium">{JSON.stringify(to)}</span>
                            </div>
                          ))}
                        </div>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {!commitResult && (
            <p className="mt-3 text-xs text-surface-muted">
              Nothing has been written yet. Review the diff above; click <strong>Apply changes</strong> to commit.
            </p>
          )}
        </div>
      )}
    </Modal>
  );
}
